import { Button, Card, Divider, Grid, Group, LoadingOverlay, Modal, NumberInput, Space, Stack, Text, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { CurrencyDollar } from "tabler-icons-react";
import { GET_USER_BALANCE, ADD_BALANCE_MUTATION, TOPUP_COMPLETED_SUB } from '../../../queries/OrdersAndWallets'
import { IS_USER_IN_ONGOING_CONTEST } from '../../../queries/contest'
import React, { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useSubscription } from '@apollo/client'
import { buildApiUrl } from '../../../config/endpoints';


export default function BalanceCard({ userid, plTrendRef, allocationRef, allocationTotal = 0 }) {

	const [userBalance, setUserBalance] = useState(0)
	const [userLocked, setUserLocked] = useState(0)
	const [userTotalEquity, setUserTotalEquity] = useState(0)
	const [cumuPL, setCumuPL] = useState(0)
	const [ytm, setYtm] = useState(0)
	const [showModal, setShowModal] = useState(false)
	const [modalMessage, setModalMessage] = useState("")
	const [isLoading, setIsLoading] = useState(false)
	const [date, setDate] = useState("")
	const [color, setColor] = useState("green")
	const [isInOngoingContest, setIsInOngoingContest] = useState(false)
	const [submitted, setSubmitted] = useState(false)
	
	const { data: queryData, refetch } = useQuery(GET_USER_BALANCE, {
		variables: { userid },
		skip: !userid,
		notifyOnNetworkStatusChange: true,
		fetchPolicy: 'cache-and-network',
	});

	const { loading: contestLoading, error: contestError, data: contestData, refetch: refetchContestStatus } = useQuery(IS_USER_IN_ONGOING_CONTEST, { 
		variables: { userid },
		skip: !userid,
		notifyOnNetworkStatusChange: true,
		fetchPolicy: 'cache-and-network',
		pollInterval: 30000 // Poll every 30 seconds to check contest status
	});

	const cumulativeHandler = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await fetch(buildApiUrl('/user/cumulativepl') + '?' + new URLSearchParams({ userid: userid }));
			if (!response.ok) {
				throw new Error(`Error! status: ${response.status}`);
			}
			const result = await response.json();
			setCumuPL(result.cumu);
			setYtm(parseFloat(result.cumu) > 0 ? result.ytm : -result.ytm);
			setUserTotalEquity(Number(result.totalEquity))
			setColor(parseFloat(result.cumu) > 0 ? "green" : "red");
		} catch (err) {
			return err
		} finally {
			setIsLoading(false);
		}
	}, [userid]);

	useSubscription(TOPUP_COMPLETED_SUB, {
		variables: { userid: String(userid) },
		skip: !userid,
		onData: ({ data }) => {
			const nb = data?.data?.topupCompleted?.newBalance;
			if (nb) {
				setUserBalance(parseFloat(nb));
				cumulativeHandler();
				plTrendRef?.current?.refresh();
				allocationRef?.current?.refresh();
			}
		}
	});

	const [addBalanceMutation, { loading: addBalanceLoading }] = useMutation(ADD_BALANCE_MUTATION, {
		onCompleted: (data) => {
			setSubmitted(false);
			if (data.addBalance.status === 200) {
				setModalMessage(`Successfully topped up $${form.values.amount}! All metrics updated.`);
				setShowModal(true);
				form.reset();
				refetch();
				cumulativeHandler();
				plTrendRef?.current?.refresh();
				allocationRef?.current?.refresh();
			} else {
				setModalMessage(data.addBalance.error || "Top up failed");
				setShowModal(true);
			}
		},
		onError: (error) => {
			setSubmitted(false);
			console.error('Top up error:', error);
			setModalMessage(error.message || "Network error occurred. Please try again.");
			setShowModal(true);
		}
	});

	useEffect(() => {
		let d = new Date()
		let month = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'][d.getMonth()]
		setDate(d.getDate() + ' ' + month + ' ' + d.getFullYear());
	}, []);

	const form = useForm({
		initialValues: { amount: 0.00 },
		validate: {
			amount: (value) => {
				if (!value || value <= 0) return 'Amount must be greater than 0';
				if (value > 100000) return 'Maximum amount is $100,000';
				if (userBalance + value > 1000000) return 'Would exceed balance limit of $1,000,000';
				return null;
			}
		}
	});

	const topupHandler = async () => {
		console.log('Top-up handler called. User ID:', userid, 'isInOngoingContest:', isInOngoingContest);

		if (submitted) {
			return;
		}

		if (!userid) {
			setModalMessage("User ID not available");
			setShowModal(true);
			return;
		}

		// Check if user is in any contest
		if (isInOngoingContest) {
			setModalMessage("Top-up is disabled while you are participating in a contest. Your balance is fixed at $100,000 during contests.");
			setShowModal(true);
			return;
		}

		if (!form.values.amount || form.values.amount <= 0) {
			setModalMessage("Please enter a valid amount greater than 0");
			setShowModal(true);
			return;
		}

		if (form.values.amount > 100000) {
			setModalMessage("Maximum top-up amount is $100,000");
			setShowModal(true);
			return;
		}

		// Check if the top-up would exceed the balance limit
		const potentialNewBalance = userBalance + form.values.amount;
		if (potentialNewBalance > 1000000) {
			setModalMessage(`Top-up would exceed balance limit. Current balance: $${userBalance.toLocaleString()}. Maximum allowed: $1,000,000.`);
			setShowModal(true);
			return;
		}

		setSubmitted(true);
		try {
			await addBalanceMutation({
				variables: {
					userid: userid,
					amount: parseFloat(form.values.amount)
				}
			});
		} catch (error) {
			// Error is handled in the mutation's onError callback
			console.error('Top up handler error:', error);
		}
	};

	const prevBalanceRef = React.useRef(null);
	useEffect(() => {
		try {
			if (queryData !== undefined && queryData.getUserBalance) {
				const res = queryData.getUserBalance;
				const newBalance = res[0];
				setUserBalance(newBalance);
				setUserLocked(res[1]);
				// When balance changes (e.g. contest join/exit), refresh all derived metrics.
				// Skip on initial load (prevBalanceRef is null) to avoid double-fetching with
				// the cumulativeHandler useEffect that runs on mount.
				if (prevBalanceRef.current !== null && prevBalanceRef.current !== newBalance) {
					cumulativeHandler();
					plTrendRef?.current?.refresh();
					allocationRef?.current?.refresh();
				}
				prevBalanceRef.current = newBalance;
			}
		} catch (error) { }
	}, [queryData, cumulativeHandler, plTrendRef, allocationRef]);

	useEffect(() => {
		try {
			console.log('Contest participation check:', {
				loading: contestLoading,
				error: contestError,
				data: contestData,
				userid: userid
			});
			
			if (contestData !== undefined && contestData.isUserInOngoingContest !== undefined) {
				console.log('Setting isInOngoingContest to:', contestData.isUserInOngoingContest);
				setIsInOngoingContest(contestData.isUserInOngoingContest);
			}
		} catch (error) { 
			console.error('Error checking contest participation:', error);
		}
	}, [contestLoading, contestError, contestData]);

	useEffect(() => {
		cumulativeHandler();
		const intervalId = setInterval(() => cumulativeHandler(), 60000);
		return () => clearInterval(intervalId);
	}, [cumulativeHandler]);

	// Refetch contest status when the window gains focus
	useEffect(() => {
		const handleFocus = () => {
			refetchContestStatus();
		};
		
		window.addEventListener('focus', handleFocus);
		return () => window.removeEventListener('focus', handleFocus);
	}, [refetchContestStatus]);

	return (
		<Card withBorder>
			<LoadingOverlay visible={isLoading || addBalanceLoading} overlayBlur={2} />
			<Stack spacing={6}>
				<Grid spacing={8}>
					<Grid.Col span={7}>
						<Text fz='xs' c="dimmed">Balance</Text>
						<Title order={2}>${userBalance.toLocaleString()}</Title>
					</Grid.Col>
					<Grid.Col span={7}>
						<Text fz='xs' c="dimmed">Locked</Text>
						<Title order={2}>${userLocked.toLocaleString()}</Title>
					</Grid.Col>
					<Grid.Col span={5}>
						<Text fz='xs' c="dimmed">Total Equity</Text>
						<Title order={2}>${(userTotalEquity || allocationTotal).toLocaleString()}</Title>
					</Grid.Col>
					<Grid.Col span={7}>
						<Text fz='xs' c="dimmed">Cumulative P/L($)</Text>
						<Title order={2} color={color}>{parseFloat(cumuPL).toLocaleString()}</Title>
					</Grid.Col>
					<Grid.Col span={5}>
						<Text fz='xs' c="dimmed">Yield to Maturity(%)</Text>
						<Title order={2} color={color}>{ytm}</Title>
					</Grid.Col>
				</Grid>
				<Space />
				<Text size="xs">As of {date}</Text>
			</Stack>
			<Divider my='md' size='xs' mx='sm' />
			<Stack spacing={6}>
				<Title order={5}>Top Up</Title>
				<NumberInput 
					label="Amount" 
					defaultValue={5000} 
					description={`From $0.01 to $100,000. Current balance: $${userBalance.toLocaleString()}`}
					precision={2} 
					min={0.01} 
					step={1000} 
					stepHoldDelay={500} 
					stepHoldInterval={100} 
					max={100000} 
					icon={<CurrencyDollar size={18} />} 
					{...form.getInputProps('amount')} 
				/>
				<Space />
				<Button
					variant='filled'
					size="sm"
					onClick={() => topupHandler()}
					disabled={!userid || addBalanceLoading || !form.values.amount || form.values.amount <= 0 || form.errors.amount || isInOngoingContest || submitted}
					loading={addBalanceLoading}
				>
					{isInOngoingContest ? 'Top Up Disabled (In Contest)' : 'Top Up'}
				</Button>
			</Stack>
			<Modal opened={showModal} onClose={() => setShowModal(false)} title="Message">
				<Text>{modalMessage}</Text>
				<Group position="right">
					<Button mt="md" onClick={() => setShowModal(false)}>Close</Button>
				</Group>
			</Modal>
		</Card>
	)
}